import {memo, useCallback, useRef, useState} from 'react'
import {XIcon} from '@primer/octicons-react'
import {Button, CounterLabel, IconButton, SegmentedControl} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {useUnifiedBatchActions, useUnifiedBatchCount} from '../../stores/layout-selectors'
import {Panel} from './Panel'
import {CompactPanel} from './CompactPanel'
import {getPanelHeading} from './panel-registry'
import {restorePanelFocusAfterClose} from './panel-focus'
import {MANAGE_BATCH_COMMENTS_HEADING_ID, ManageBatchCommentsList} from './manage-batch/ManageBatchCommentsList'
import {
  ManageBatchCommitFormCommands,
  ManageBatchCommitProvider,
  ManageBatchCommitTab,
  ManageBatchCommitTabPrimaryCta,
} from './manage-batch/ManageBatchCommitTab'
import {useManageBatchTelemetry} from './manage-batch/manage-batch-telemetry'
import styles from './ManageBatchPanel.module.css'

export type ManageBatchTab = 'commit' | 'fix'

const TAB_DEFS: Array<{value: ManageBatchTab; label: string}> = [
  {value: 'commit', label: 'Commit suggestions'},
  {value: 'fix', label: 'Fix with Copilot'},
]

const TAB_VALUES = TAB_DEFS.map(({value}) => value)

/**
 * Manage batch panel shell.
 *
 * Hosts the docked-panel surface for the unified batch experience. This file
 * delivers the structural pieces only:
 *  - Tab header (Commit suggestions | Fix with Copilot).
 *  - Shared `Comments(N)` list with per-row remove.
 *  - Footer slot framework (Discard batch / Cancel / per-tab primary CTA slot).
 *  - Empty state when the batch is empty.
 *
 * The tab-body content slots are intentionally empty placeholders for the
 * tab-content follow-ups to fill:
 *  - Commit suggestions tab content
 *  - Fix with Copilot tab content
 *  - Default-tab logic + disallowed-tab warning banner
 *  - Outdated suggestion handling
 *
 * Gated behind `unified_batch_pr_comments`. Registered in `panel-registry.tsx`
 * so it joins the enabled-panel set (and the panel-selector dropdown) when
 * the flag is on. Mounted by `DockedSidePanelContent` via `panel-render-map`
 * when the panel is active and the viewport is non-compact; falls back to a
 * Dialog in compact mode.
 */
export const ManageBatchPanel = memo(function ManageBatchPanelImpl() {
  const isFlagEnabled = isFeatureEnabled('unified_batch_pr_comments')
  const updatedPullRequestHeader = isFeatureEnabled('updated_pull_request_header')
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.ManageBatchReviewComments)
  const isCompact = useIsDockedPanelCompact()
  const {clearBatch} = useUnifiedBatchActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeManageBatchPanel = useCloseManageBatchPanel()
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const [activeTab, setActiveTab] = useState<ManageBatchTab>('commit')

  const handleSubmitted = useCallback(() => {
    clearBatch()
    closeManageBatchPanel()
  }, [clearBatch, closeManageBatchPanel])

  if (!isFlagEnabled || !isDockedPanelOpen) return null

  if (!isCompact) {
    return (
      <ActiveTabProvider onSubmitted={handleSubmitted}>
        <Panel panelName={DockedPanel.ManageBatchReviewComments}>
          <Panel.Header>
            <ManageBatchTabsHeader activeTab={activeTab} setActiveTab={setActiveTab} />
          </Panel.Header>
          <Panel.Body scrollable={false} className={styles.bodyLayout}>
            <ManageBatchBody activeTab={activeTab} />
          </Panel.Body>
        </Panel>
      </ActiveTabProvider>
    )
  }

  if (!updatedPullRequestHeader) {
    return (
      <ActiveTabProvider onSubmitted={handleSubmitted}>
        <Dialog
          initialFocusRef={closeButtonRef}
          position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
          returnFocusRef={returnFocusRef}
          onClose={closeManageBatchPanel}
          renderHeader={({dialogLabelId}) => (
            <Dialog.Header>
              <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
                <Dialog.Title id={dialogLabelId}>{getPanelHeading(DockedPanel.ManageBatchReviewComments)}</Dialog.Title>
                <IconButton
                  ref={closeButtonRef}
                  aria-label="Close manage batch panel"
                  icon={XIcon}
                  variant="invisible"
                  onClick={closeManageBatchPanel}
                />
              </div>
              <ManageBatchTabsHeader activeTab={activeTab} setActiveTab={setActiveTab} />
            </Dialog.Header>
          )}
        >
          <div className={`flex-1 overflow-hidden ${styles.bodyLayout}`}>
            <ManageBatchBody activeTab={activeTab} />
          </div>
        </Dialog>
      </ActiveTabProvider>
    )
  }

  return (
    <ActiveTabProvider onSubmitted={handleSubmitted}>
      <CompactPanel.Header>
        <ManageBatchTabsHeader activeTab={activeTab} setActiveTab={setActiveTab} />
      </CompactPanel.Header>
      <CompactPanel.Content>
        <ManageBatchBody activeTab={activeTab} />
      </CompactPanel.Content>
    </ActiveTabProvider>
  )
})

/**
 * Per-tab context wrapper. The Commit suggestions tab body and its primary
 * CTA render in separate parts of the panel's top section — the tab body and
 * the actions row — so they can't share component-local state. We wrap the
 * entire panel in a `ManageBatchCommitProvider` so both read and update the
 * same form state and submit callback. The provider is mounted unconditionally
 * (rather than only while the Commit tab is active) so the user's edited
 * commit message and description survive switching to the Fix tab and back.
 * The Fix tab doesn't consume this context, so keeping it mounted is
 * harmless; the Fix tab nests its own provider here in
 * the Fix with Copilot tab follow-up.
 */
function ActiveTabProvider({onSubmitted, children}: {onSubmitted: () => void; children: React.ReactNode}) {
  return <ManageBatchCommitProvider onSubmitted={onSubmitted}>{children}</ManageBatchCommitProvider>
}

function ManageBatchTabsHeader({
  activeTab,
  setActiveTab,
}: {
  activeTab: ManageBatchTab
  setActiveTab: (tab: ManageBatchTab) => void
}) {
  const {onTabSelected} = useManageBatchTelemetry()

  const handleChange = useCallback(
    (selectedIndex: number) => {
      const next = TAB_VALUES[selectedIndex] ?? 'commit'
      setActiveTab(next)
      onTabSelected(next)
    },
    [setActiveTab, onTabSelected],
  )

  return (
    <SegmentedControl aria-label="Manage batch action mode" fullWidth onChange={handleChange} className={styles.tabs}>
      {TAB_DEFS.map(({value, label}) => (
        <SegmentedControl.Button key={value} selected={activeTab === value} data-testid={`manage-batch-tab-${value}`}>
          {label}
        </SegmentedControl.Button>
      ))}
    </SegmentedControl>
  )
}

function ManageBatchBody({activeTab}: {activeTab: ManageBatchTab}) {
  const count = useUnifiedBatchCount()

  // The top section holds the active tab's form fields and the actions row
  // (Discard / Cancel / primary CTA). For the Commit tab we wrap it in
  // `ManageBatchCommitFormCommands` so ⌘/Ctrl+Enter submits the batch from
  // anywhere in the form area — mirroring `ApplySuggestionDialog`.
  const topSection = (
    <div className={styles.panelTop}>
      <div data-testid={`manage-batch-tab-body-${activeTab}`}>
        {activeTab === 'commit' && <ManageBatchCommitTab />}
        {/* Fix tab body placeholder — the Fix with Copilot tab follow-up fills this slot. */}
      </div>
      <ManageBatchActions activeTab={activeTab} />
    </div>
  )

  return (
    <>
      {activeTab === 'commit' ? (
        <ManageBatchCommitFormCommands>{topSection}</ManageBatchCommitFormCommands>
      ) : (
        topSection
      )}
      <div className={styles.panelScrollable}>
        <h3 className={styles.commentsHeading} id={MANAGE_BATCH_COMMENTS_HEADING_ID} tabIndex={-1}>
          Batched comments <CounterLabel className="ml-1">{count}</CounterLabel>
        </h3>
        <ManageBatchCommentsList />
      </div>
    </>
  )
}

function ManageBatchActions({activeTab}: {activeTab: ManageBatchTab}) {
  const closeManageBatchPanel = useCloseManageBatchPanel()
  const {clearBatch} = useUnifiedBatchActions()
  const {onPanelClose} = useManageBatchTelemetry()

  const handleDiscard = useCallback(() => {
    onPanelClose()
    closeManageBatchPanel()
    clearBatch()
  }, [clearBatch, closeManageBatchPanel, onPanelClose])

  const handleCancel = useCallback(() => {
    onPanelClose()
    closeManageBatchPanel()
  }, [closeManageBatchPanel, onPanelClose])

  return (
    <div className={styles.actions}>
      <Button variant="danger" onClick={handleDiscard}>
        Discard batch
      </Button>
      <div className={styles.actionsRight}>
        <Button variant="default" onClick={handleCancel}>
          Cancel
        </Button>
        <div data-testid={`manage-batch-primary-cta-slot-${activeTab}`}>
          {activeTab === 'commit' && <ManageBatchCommitTabPrimaryCta />}
          {/* Fix tab CTA placeholder — the Fix with Copilot tab follow-up renders `Fix and commit ▾` here. */}
        </div>
      </div>
    </div>
  )
}

function useCloseManageBatchPanel() {
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()

  return useCallback(() => {
    restorePanelFocusAfterClose(returnFocusRef.current)
    closePanel()
  }, [closePanel, returnFocusRef])
}
